import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogOut, Eye, Paperclip, X, Download, MessageSquare, Filter, Search, Trash2, Send, Eye as EyeIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import LoadingQuip from "@/components/LoadingQuip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useTheme } from "@/contexts/ThemeContext";
import DarkModeToggle from "@/components/DarkModeToggle";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

// Thumbnail Component for Question Cards  
function MediaThumbnail({ attachment, getAuthenticatedFileUrl }) {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  
  const isImage = attachment.file_type === 'image' || 
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(attachment.filename);
  const isVideo = attachment.file_type === 'video' || 
    /\.(mp4|mov|avi|wmv|webm)$/i.test(attachment.filename);
  
  useEffect(() => {
    if (isImage || isVideo) {
      getAuthenticatedFileUrl(attachment.file_path).then(url => {
        setThumbnailUrl(url);
      });
    }
  }, [attachment.file_path, isImage, isVideo, getAuthenticatedFileUrl]);
  
  if (!thumbnailUrl) return null;
  
  return (
    <div className="relative w-16 h-16 rounded overflow-hidden border border-gray-200 flex-shrink-0">
      {isImage ? (
        <img
          src={thumbnailUrl}
          alt="thumbnail"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
          </svg>
        </div>
      )}
    </div>
  );
}

// File Preview Component
function FilePreviewCard({ attachment, getAuthenticatedFileUrl, downloadFile, formatFileSize }) {
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdfModal, setShowPdfModal] = useState(false);
  
  const isImage = attachment.file_type === 'image' || 
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(attachment.filename);
  const isVideo = attachment.file_type === 'video' || 
    /\.(mp4|mov|avi|wmv|webm)$/i.test(attachment.filename);
  const isPdf = attachment.file_type === 'pdf' || 
    /\.pdf$/i.test(attachment.filename);
  
  // Auto-load media when component mounts
  useEffect(() => {
    let isMounted = true;
    
    if (isImage || isVideo || isPdf) {
      getAuthenticatedFileUrl(attachment.file_path).then(url => {
        if (isMounted) {
          setFileUrl(url);
          setLoading(false);
        }
      }).catch(err => {
        console.error('[FilePreviewCard] Error loading:', err);
        if (isMounted) {
          setFileUrl(null);
          setLoading(false);
        }
      });
    } else {
      setLoading(false);
    }
    
    return () => {
      isMounted = false;
    };
  }, [attachment.file_path]);
  
  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      {/* File Header */}
      <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
        <div className="flex items-center space-x-3">
          <Paperclip className="h-4 w-4 text-gray-500 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{attachment.filename}</p>
            <p className="text-xs text-gray-500">
              {formatFileSize(attachment.file_size)} • {attachment.file_type}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => downloadFile(attachment.file_path, attachment.filename)}
          className="flex-shrink-0"
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
      
      {/* File Preview */}
      <div className="p-3 bg-gray-50">
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingQuip size="sm" />
          </div>
        ) : fileUrl ? (
          <>
            {isImage && (
              <div className="flex justify-center">
                <img
                  src={fileUrl}
                  alt={attachment.filename}
                  className="max-w-full h-auto rounded border border-gray-300 shadow-sm"
                  style={{ maxHeight: '500px' }}
                />
              </div>
            )}
            {isVideo && (
              <video
                key={fileUrl}
                src={fileUrl}
                controls
                className="w-full h-auto rounded border border-gray-300 shadow-sm"
                style={{ maxHeight: '500px' }}
              >
                Your browser does not support video playback.
              </video>
            )}
            {isPdf && (
              <div className="space-y-3">
                {/* PDF Thumbnail Preview */}
                {fileUrl && (
                  <div 
                    className="relative bg-white rounded border-2 border-gray-200 overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
                    onClick={() => setShowPdfModal(true)}
                    style={{ height: '500px' }}
                  >
                    <embed
                      src={`${fileUrl}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                      type="application/pdf"
                      className="w-full h-full pointer-events-none"
                    />
                    
                    {/* Overlay to make entire area clickable */}
                    <div 
                      className="absolute inset-0 cursor-pointer"
                      onClick={() => setShowPdfModal(true)}
                      title="Click to view full PDF"
                    />
                  </div>
                )}
                
                {/* View PDF Button */}
                <Button
                  onClick={() => setShowPdfModal(true)}
                  className="w-full"
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  View Full PDF
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Paperclip className="h-12 w-12 mx-auto mb-2 text-gray-400" />
            <p className="text-sm font-medium">Unable to load media</p>
            <p className="text-xs mt-1">Please try downloading the file</p>
          </div>
        )}
      </div>
      
      {/* PDF Viewer Modal */}
      {isPdf && showPdfModal && fileUrl && (
        <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
          <DialogContent className="max-w-5xl h-[90vh] overflow-hidden p-0">
            <DialogHeader className="px-6 pt-6 pb-2">
              <DialogTitle>{attachment.filename}</DialogTitle>
            </DialogHeader>
            <div className="w-full h-full px-6 pb-6" style={{ height: 'calc(90vh - 80px)' }}>
              <object
                data={fileUrl}
                type="application/pdf"
                className="w-full h-full rounded border border-gray-300"
              >
                <div className="flex flex-col items-center justify-center h-full p-8 text-center text-gray-500">
                  <p className="font-medium mb-4">Unable to display PDF in browser.</p>
                  <Button
                    onClick={() => downloadFile(attachment.file_path, attachment.filename)}
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF Instead
                  </Button>
                </div>
              </object>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function AdminCurbsideConsult({ user, logout }) {
  const { isDarkMode } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeView, setActiveView] = useState('recent'); // 'recent' or 'archive'
  const [responseText, setResponseText] = useState('');
  const [tags, setTags] = useState('');
  const [responseFiles, setResponseFiles] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const navigate = useNavigate();
  
  const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB
  const [fileUrls, setFileUrls] = useState({});

  useEffect(() => {
    fetchQuestions();
  }, [statusFilter]);

  // Fetch authenticated file and create blob URL
  const getAuthenticatedFileUrl = useCallback(async (filePath) => {
    if (fileUrls[filePath]) {
      return fileUrls[filePath];
    }

    try {
      const pathParts = filePath.split('/');
      const fileName = pathParts[pathParts.length - 1];
      const subfolder = pathParts[pathParts.length - 2];
      
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      
      const response = await axios.get(
        `${API}/curbside-consult/files/${subfolder}/${fileName}`,
        {
          headers,
          responseType: 'blob'
        }
      );
      
      const blobUrl = URL.createObjectURL(response.data);
      setFileUrls(prev => ({ ...prev, [filePath]: blobUrl }));
      return blobUrl;
    } catch (error) {
      console.error("Error fetching file:", error);
      return null;
    }
  }, [fileUrls]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      const response = await axios.get(
        `${API}/curbside-consult/questions?status=${statusFilter}`,
        { headers }
      );
      setQuestions(response.data);
    } catch (error) {
      toast.error("Error loading questions");
    } finally {
      setLoading(false);
    }
  };

  const viewQuestionDetail = async (questionId) => {
    try {
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      const response = await axios.get(
        `${API}/curbside-consult/question/${questionId}`,
        { headers }
      );
      setSelectedQuestion(response.data);
      setResponseText(response.data.admin_response_text || '');
      setTags(response.data.tags ? response.data.tags.join(', ') : '');
    } catch (error) {
      toast.error("Error loading question details");
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Check file sizes
    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      toast.error(`Files must be less than 500MB. ${oversizedFiles[0].name} is too large.`);
      return;
    }
    
    setResponseFiles([...responseFiles, ...selectedFiles]);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("Video must be less than 500MB");
        return;
      }
      setVideoFile(file);
    }
  };

  const removeFile = (index) => {
    setResponseFiles(responseFiles.filter((_, i) => i !== index));
  };

  const handleSubmitResponse = async (newStatus = 'answered') => {
    if (!selectedQuestion) return;
    
    if (!responseText.trim() && responseFiles.length === 0 && !videoFile) {
      toast.error("Please provide a response or upload files");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const formData = new FormData();
      if (responseText.trim()) {
        formData.append('response_text', responseText);
      }
      if (tags.trim()) {
        formData.append('tags', tags);
      }
      formData.append('status', newStatus);
      
      // Send current attachments (to preserve what wasn't deleted)
      if (selectedQuestion.admin_response_attachments) {
        formData.append('current_attachments', JSON.stringify(selectedQuestion.admin_response_attachments));
      }
      
      // If video was deleted, explicitly tell backend
      if (selectedQuestion.admin_response_video === null) {
        formData.append('delete_video', 'true');
      }
      
      if (videoFile) {
        formData.append('video_file', videoFile);
      }
      
      responseFiles.forEach(file => {
        formData.append('response_files', file);
      });
      
      const currentToken = localStorage.getItem("session_token");
      await axios.put(
        `${API}/curbside-consult/question/${selectedQuestion.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success("Response submitted successfully!");
      
      // Reset form
      setResponseText('');
      setTags('');
      setResponseFiles([]);
      setVideoFile(null);
      setSelectedQuestion(null);
      
      // Refresh questions list
      fetchQuestions();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;
    
    try {
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      await axios.delete(
        `${API}/curbside-consult/question/${questionToDelete.id}`,
        { headers }
      );
      
      toast.success("Question deleted successfully");
      setQuestionToDelete(null);
      
      if (selectedQuestion && selectedQuestion.id === questionToDelete.id) {
        setSelectedQuestion(null);
      }
      
      fetchQuestions();
    } catch (error) {
      toast.error("Error deleting question");
    }
  };

  const downloadFile = async (filePath, filename) => {
    try {
      const pathParts = filePath.split('/');
      const file = pathParts[pathParts.length - 1];
      const subfolder = pathParts[pathParts.length - 2];
      
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      const response = await axios.get(
        `${API}/curbside-consult/files/${subfolder}/${file}`,
        {
          headers,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("File downloaded");
    } catch (error) {
      toast.error("Error downloading file");
    }
  };

  const handleDeleteAttachment = (attachmentIndex) => {
    if (!selectedQuestion) return;
    
    const updatedAttachments = selectedQuestion.admin_response_attachments.filter(
      (_, idx) => idx !== attachmentIndex
    );
    
    setSelectedQuestion({
      ...selectedQuestion,
      admin_response_attachments: updatedAttachments
    });
    
    toast.success("Attachment removed. Click 'Save' to apply changes.");
  };

  const handleDeleteVideo = () => {
    if (!selectedQuestion) return;
    
    setSelectedQuestion({
      ...selectedQuestion,
      admin_response_video: null
    });
    
    setVideoFile(null);
    
    toast.success("Video removed. Click 'Save' to apply changes.");
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get all unique tags
  const allTags = [...new Set(questions.flatMap(q => q.tags || []))];

  // Filter questions by search and tags first
  const searchFilteredQuestions = questions.filter(q => {
    const matchesSearch = searchTerm === '' || 
      q.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.student_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.admin_response_text?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTags = selectedTags.length === 0 || 
      selectedTags.some(tag => q.tags?.includes(tag));
    
    return matchesSearch && matchesTags;
  });

  // Separate archived and non-archived questions
  const archivedQuestions = searchFilteredQuestions.filter(q => q.status === 'archived');
  const nonArchivedQuestions = searchFilteredQuestions.filter(q => q.status !== 'archived');
  
  // For recent view, show 10 most recent non-archived questions
  const recentQuestions = nonArchivedQuestions.slice(0, 10);
  
  // Select which questions to display based on active view
  const displayedQuestions = activeView === 'recent' ? recentQuestions : archivedQuestions;

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'answered': return 'bg-green-100 text-green-800 border-green-300';
      case 'archived': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen" style={{ background: isDarkMode ? '#092036' : 'linear-gradient(to bottom right, #eff6ff, #eef2ff, #f5f3ff)' }}>
      {/* Header */}
      <header className="shadow-sm sticky top-0 z-50" style={{ backgroundColor: isDarkMode ? '#092036' : 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-auto cursor-pointer" onClick={() => navigate('/admin')} style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
              <h1 className="text-xl sm:text-2xl font-bold" style={{ color: isDarkMode ? '#ffffff' : '#111827' }}>Curbside Consult - Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <DarkModeToggle />
              <Button variant="ghost" onClick={() => navigate('/admin')} style={{ color: isDarkMode ? '#ffffff' : undefined }}>
                Back to Admin
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2"
              >
                <Eye className="h-4 w-4" />
                <span className="hidden sm:inline">View as Student</span>
              </Button>
              <Button variant="ghost" onClick={logout} className="flex items-center space-x-2">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* View Toggle */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex space-x-2">
            <Button
              variant={activeView === 'recent' ? 'default' : 'outline'}
              onClick={() => setActiveView('recent')}
            >
              Recent ({recentQuestions.length})
            </Button>
            <Button
              variant={activeView === 'archive' ? 'default' : 'outline'}
              onClick={() => setActiveView('archive')}
            >
              Archive ({archivedQuestions.length})
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            {activeView === 'recent' 
              ? `Showing ${displayedQuestions.length} most recent non-archived questions`
              : `${displayedQuestions.length} archived questions`}
          </p>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6 light-form-card">
          <div className="space-y-4">
            {/* Status Filter and Search */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block" style={{ color: isDarkMode ? '#374151' : undefined }}>Status Filter</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Questions</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="answered">Answered</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="mb-2 block" style={{ color: isDarkMode ? '#374151' : undefined }}>Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search questions, students, emails..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Tag Filters */}
            {allTags.length > 0 && (
              <div>
                <Label className="flex items-center space-x-2 mb-2" style={{ color: isDarkMode ? '#374151' : undefined }}>
                  <Filter className="h-4 w-4" />
                  <span>Filter by Tags</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => (
                    <Badge
                      key={tag}
                      variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
            <div className="text-sm" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>Total Questions</div>
            <div className="text-2xl font-bold" style={{ color: isDarkMode ? '#ffffff' : '#111827' }}>{questions.length}</div>
          </Card>
          <Card className="p-4" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
            <div className="text-sm" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>Pending</div>
            <div className="text-2xl font-bold text-yellow-600">
              {questions.filter(q => q.status === 'pending').length}
            </div>
          </Card>
          <Card className="p-4" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
            <div className="text-sm" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>Answered</div>
            <div className="text-2xl font-bold text-green-600">
              {questions.filter(q => q.status === 'answered').length}
            </div>
          </Card>
          <Card className="p-4" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
            <div className="text-sm" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>Archived</div>
            <div className="text-2xl font-bold" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
              {questions.filter(q => q.status === 'archived').length}
            </div>
          </Card>
        </div>

        {/* Questions List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingQuip size="lg" />
          </div>
        ) : displayedQuestions.length === 0 ? (
          <Card className="p-12 text-center" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
            <MessageSquare className="h-12 w-12 mx-auto mb-4" style={{ color: isDarkMode ? '#ffffff' : '#9ca3af' }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: isDarkMode ? '#ffffff' : '#111827' }}>
              {activeView === 'archive' ? 'No Archived Questions' : 'No Questions Found'}
            </h3>
            <p style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
              {activeView === 'archive' && !searchTerm && selectedTags.length === 0
                ? "Older questions will appear here once there are more than 10 questions"
                : searchTerm || selectedTags.length > 0
                ? "No questions match your search criteria"
                : statusFilter === 'pending'
                ? "No pending questions at the moment"
                : "No questions yet"}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {displayedQuestions.map((question) => (
              <Card
                key={question.id}
                className="p-6 hover:shadow-lg transition-shadow"
                style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}
              >
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2 flex-wrap gap-2">
                        <Badge className={`${getStatusColor(question.status)} border`}>
                          {question.status}
                        </Badge>
                        <span className="font-semibold" style={{ color: isDarkMode ? '#ffffff' : '#111827' }}>
                          {question.student_name}
                        </span>
                        <span className="text-sm" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                          ({question.student_email})
                        </span>
                        <span className="text-sm" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                          • {formatDate(question.created_at)}
                        </span>
                        <span className="text-sm" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                          • {question.view_count} views
                        </span>
                      </div>
                      <p className="line-clamp-2" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                        {question.question_text}
                      </p>
                    </div>
                  </div>

                  {/* Media Thumbnails */}
                  {question.attachments && question.attachments.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {question.attachments.slice(0, 3).map((attachment, idx) => (
                        <MediaThumbnail
                          key={idx}
                          attachment={attachment}
                          getAuthenticatedFileUrl={getAuthenticatedFileUrl}
                        />
                      ))}
                      {question.attachments.length > 3 && (
                        <div className="w-16 h-16 rounded border flex items-center justify-center text-xs font-medium" style={{ borderColor: isDarkMode ? '#1e3a5f' : '#e5e7eb', backgroundColor: isDarkMode ? '#1e3a5f' : '#f3f4f6', color: isDarkMode ? '#ffffff' : '#000000' }}>
                          +{question.attachments.length - 3}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tags */}
                  {question.tags && question.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {question.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center space-x-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => viewQuestionDetail(question.id)}
                    >
                      {question.status === 'pending' ? 'Respond' : 'View/Edit'}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setQuestionToDelete(question)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Question Detail & Response Modal */}
      <Dialog open={!!selectedQuestion} onOpenChange={() => {
        setSelectedQuestion(null);
        setResponseText('');
        setTags('');
        setResponseFiles([]);
        setVideoFile(null);
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto light-form-card">
          {selectedQuestion && (
            <div className="space-y-6">
              <DialogHeader>
                <DialogTitle className="text-2xl">Question & Response</DialogTitle>
              </DialogHeader>

              {/* Question Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Badge className={`${getStatusColor(selectedQuestion.status)} border`}>
                      {selectedQuestion.status}
                    </Badge>
                    <span className="font-semibold">{selectedQuestion.student_name}</span>
                  </div>
                  <span className="text-sm text-gray-600">
                    {formatDate(selectedQuestion.created_at)}
                  </span>
                </div>

                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-600">Student Email:</span>
                  <span className="text-sm text-gray-900 ml-2">{selectedQuestion.student_email}</span>
                </div>

                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-600">Actual Name:</span>
                  <span className="text-sm text-gray-900 ml-2">{selectedQuestion.actual_student_name}</span>
                </div>

                <p className="text-gray-900 whitespace-pre-wrap mt-3">
                  {selectedQuestion.question_text}
                </p>

                {/* Question Attachments */}
                {selectedQuestion.attachments && selectedQuestion.attachments.length > 0 && (
                  <div className="mt-4">
                    <Label className="mb-2 block">Student Attachments:</Label>
                    <div className="space-y-3">
                      {selectedQuestion.attachments.map((attachment, idx) => (
                        <FilePreviewCard
                          key={idx}
                          attachment={attachment}
                          getAuthenticatedFileUrl={getAuthenticatedFileUrl}
                          downloadFile={downloadFile}
                          formatFileSize={formatFileSize}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Response Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Your Response</h3>

                {/* Response Text */}
                <div>
                  <Label htmlFor="responseText">Response Text</Label>
                  <Textarea
                    id="responseText"
                    placeholder="Enter your detailed response..."
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows={8}
                    className="mt-1"
                  />
                </div>

                {/* Tags */}
                <div>
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    type="text"
                    placeholder="e.g., Periodontal Disease, Diabetes, Patient Management"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Create custom tags to categorize this Q&A
                  </p>
                </div>

                {/* Video Upload */}
                <div>
                  <Label>Response Video (Optional)</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      id="videoInput"
                      accept="video/*"
                      onChange={handleVideoChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('videoInput').click()}
                      className="w-full sm:w-auto flex items-center space-x-2"
                    >
                      <Paperclip className="h-4 w-4" />
                      <span>
                        {videoFile ? `Video: ${videoFile.name}` : 'Add Video'}
                      </span>
                    </Button>
                    {videoFile && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setVideoFile(null)}
                        className="ml-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      Max 500MB. All video formats accepted.
                    </p>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <Label>Response Attachments (Optional)</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      id="fileInput"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('fileInput').click()}
                      className="w-full sm:w-auto flex items-center space-x-2"
                    >
                      <Paperclip className="h-4 w-4" />
                      <span>Add Files ({responseFiles.length})</span>
                    </Button>
                    <p className="text-sm text-gray-500 mt-1">
                      All file types accepted. Max 500MB per file.
                    </p>
                  </div>

                  {/* File List */}
                  {responseFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {responseFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <Paperclip className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="ml-2 flex-shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Existing Response */}
                {selectedQuestion.admin_response_text && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2">Current Response:</h4>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedQuestion.admin_response_text}
                    </p>
                    
                    {selectedQuestion.admin_response_attachments &&
                      selectedQuestion.admin_response_attachments.length > 0 && (
                        <div className="mt-3">
                          <Label className="mb-2 block text-sm">Current Attachments:</Label>
                          <div className="space-y-2">
                            {selectedQuestion.admin_response_attachments.map((attachment, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200"
                              >
                                <div className="flex items-center space-x-2">
                                  <Paperclip className="h-3 w-3 text-blue-600" />
                                  <span className="text-sm">{attachment.filename}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => downloadFile(attachment.file_path, attachment.filename)}
                                    title="Download"
                                  >
                                    <Download className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteAttachment(idx)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Delete"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {selectedQuestion.admin_response_video && (
                      <div className="mt-3">
                        <Label className="mb-2 block text-sm">Current Video:</Label>
                        <div className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-200">
                          <div className="flex items-center space-x-2">
                            <svg className="h-4 w-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                            </svg>
                            <span className="text-sm">Response Video</span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleDeleteVideo}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete Video"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button
                    onClick={() => handleSubmitResponse('answered')}
                    disabled={submitting}
                    className="flex items-center space-x-2"
                  >
                    {submitting ? (
                      <>
                        <LoadingQuip size="sm" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Save & Publish</span>
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => handleSubmitResponse('pending')}
                    disabled={submitting}
                    variant="outline"
                  >
                    Save as Draft
                  </Button>

                  <Button
                    onClick={() => handleSubmitResponse('archived')}
                    disabled={submitting}
                    variant="outline"
                  >
                    Archive
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!questionToDelete} onOpenChange={() => setQuestionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this question and all associated responses. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteQuestion} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
